import { useGetPortCatalogFilterQuery } from 'app/api/marine-infrastructure';
import { LegacyRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Filters } from 'app/pages/PortsPage/PortsPage';
import { CheckboxAll } from './CheckboxAll';

import { Button } from '../Button';
import { Input } from '../Input';
import { CheckboxOption } from './CheckboxOption';
import { FilterFormData } from './FilterPanel';
import { Icon } from '../Icon';
import { useTranslation } from 'react-i18next';
import { useGetAllValue } from 'app/hooks/useGetAllValue';
import clsx from 'clsx';

import styles from './FilterPanel.module.scss';

export type PortFilterFormData = Pick<FilterFormData, 'touched' | 'country' | 'countryAll'>;

interface Props {
  isOpen?: boolean;
  filters?: Filters | null;
  onClose: () => void;
  innerRef?: LegacyRef<HTMLDivElement>;
  handleFilters: React.Dispatch<React.SetStateAction<Filters | null>>;
}

export const PortsFilterPanel = ({ onClose, innerRef, filters, handleFilters }: Props) => {
  const { i18n, t } = useTranslation();
  const language = i18n.language;
  const { data, refetch } = useGetPortCatalogFilterQuery();
  const allCountries = useGetAllValue(data?.filter?.country?.values);

  const [countryList, setCountryList] = useState(data?.filter?.country?.values);
  const [inputValue, setInputValue] = useState<string>('');

  useEffect(() => {
    if (data?.filter?.country?.values) {
      setCountryList(data.filter.country.values);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [language]);

  const initialValues = useMemo(
    () => ({
      touched: false,
      country: allCountries,
      countryAll: true,
    }),
    [allCountries],
  );

  const initialFormValue = useMemo(() => {
    if (filters) {
      return {
        touched: filters.touched || false,
        country: filters.country || [],
        countryAll: filters.countryAll,
      };
    }

    return {
      touched: false,
      country: allCountries || [],
      countryAll: true,
    };
  }, [filters, allCountries]);

  const handleSubmit = useCallback(
    (formData: PortFilterFormData) => {
      const isEq = initialValues.countryAll === formData.countryAll;
      const { country, countryAll } = formData;

      handleFilters({
        touched: !isEq,
        country,
        countryAll,
      });
      onClose();
    },
    [initialFormValue, filters],
  );

  const formik = useFormik<PortFilterFormData>({
    enableReinitialize: true,
    initialValues: initialFormValue,
    onSubmit: handleSubmit,
  });

  const handleReset = useCallback(() => {
    formik.setValues({
      touched: false,
      country: allCountries || [],
      countryAll: true,
    });
  }, [allCountries]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const inputValue: string = e.target.value.toLowerCase();
    const filtered = data?.filter?.country?.values?.filter((value) => {
      const name = value?.name?.toLowerCase();
      const code = (value?.values?.[0] || '').toLowerCase();
      return name?.includes(inputValue) || code.includes(inputValue);
    });
    setCountryList(filtered);
  };
  const checkboxGroupCountry = useMemo(() => {
    return countryList?.map((countryValue) => {
      return (
        <CheckboxOption<PortFilterFormData>
          key={countryValue.name}
          value={countryValue}
          name="country"
          formValues={formik.values}
          formSetField={formik.setFieldValue}
        />
      );
    });
  }, [data, allCountries, formik, countryList]);
  return (
    <div className={styles.root}>
      <div className={styles.dialog_modal_s} ref={innerRef}>
        <div className={styles.panel_header}>
          <p className={styles.heading}>{t('FilterPanelHeading')}</p>
          <div onClick={onClose} className={styles.close_button}>
            <Icon name="close-16" size="16" />
          </div>
        </div>
        <div className={styles.form_container_s}>
          {!data && <div className={styles.loader} />}
          {data && (
            <form onSubmit={formik.handleSubmit} className={styles.form_s}>
              <div className={styles.container_s}>
                <div className={clsx(styles.checkboxGroup, styles.countryFilter)}>
                  <label className={styles.groupName} htmlFor="country">
                    {`${t('FilterPanelLabelCountries')}:`}
                  </label>

                  <CheckboxAll<PortFilterFormData>
                    name="country"
                    allValues={allCountries}
                    formValues={formik.values}
                    formHandleChange={formik.handleChange}
                    formSetField={formik.setFieldValue}
                  />
                  <Input
                    type="search"
                    onChange={handleInputChange}
                    placeholder={t('SearchPlaceholder')}
                    className={styles.input}
                    value={inputValue}
                  />
                  <div className={styles.listGroup}>{checkboxGroupCountry}</div>
                </div>
              </div>

              <div className={styles.form_actions}>
                <Button
                  className={styles.action_button}
                  type="button"
                  label={t('FilterPanelButtonReset')}
                  variant="general"
                  onClick={handleReset}
                />
                <div>
                  <Button className={styles.action_button} label={t('FilterPanelButtonSubmit')} variant="general" type="submit" />
                  <Button
                    className={styles.action_button}
                    type="button"
                    label={t('ButtonLabelDiscard')}
                    variant="general"
                    onClick={onClose}
                  />
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
